import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";
import {
  axiosKycServiceClient,
  getSessionId,
} from "../../../services/kyc.service";
import { catchBody } from "../../Global/Errors/ErrorAlert";
import { globalStore } from "../../Global/GlobalContext/GlobalContext";
import { kycAvailableSteps, KycForm, KycStep } from "./kycAvailableSteps";

const defaultStepsConfiguration = [
  KycStep.PhoneAndEmail,
  KycStep.ExternalKycProvider,
  KycStep.ThankYou,
];

interface KycState {
  currentForm: KycForm;
  email: string;
  phoneNumber: string;
  contactInfoSessionId: string;
  clientSessionId: string;
  stepsConfiguration: KycStep[];
  currentStepIndex: number;
  currentInternalStepIndex: number;
  phoneRegistrationId: string;
  phoneNumberVerificationCode: string;
  emailRegistrationId: string;
  emailVerificationCode: string;
  percent: number;
  currentAbsoluteStep: number;
  termsAndConditions: boolean;
  agreement: boolean;
  creditBureau: boolean;
  localId?: string;
  kycProviderUrls?: { [id: string]: string };
  filboMobileRedirectUrl?: string;
  contactInfoClientSessionId?: string;
}

const initialState: KycState = {
  currentForm: KycForm.PhoneAndEmailCollection,
  email: "",
  phoneNumber: "",
  contactInfoSessionId: "",
  clientSessionId: "",
  stepsConfiguration: defaultStepsConfiguration,
  currentStepIndex: -1,
  currentInternalStepIndex: -1,
  phoneRegistrationId: "",
  phoneNumberVerificationCode: "",
  emailRegistrationId: "",
  emailVerificationCode: "",
  percent: 0,
  currentAbsoluteStep: 0,
  termsAndConditions: false,
  agreement: false,
  creditBureau: false,
};

interface KycContextValue {
  kycState: KycState;
  setKycState: (action: KycAction) => void;
  advanceKyc: () => void;
  setKycFieldState: <K extends keyof KycState>(
    field: K,
    value?: KycState[K]
  ) => void;
  startNewKycClientSession: (steps?: []) => Promise<any> | undefined;
  continueKycClientSession: (
    clientSessionId: string,
    contactInfoSessionId: string,
    steps?: KycStep[]
  ) => void;
  appInsightsTrackEvent: (eventData: any) => void;
}

export const kycStore = createContext<KycContextValue>({} as KycContextValue);

interface Props {
  children: React.ReactNode;
}

let appInsightsClient: ApplicationInsights;

const { Provider } = kycStore;

function generateUUID() {
  var d = new Date().getTime(); //Timestamp
  var d2 = (performance && performance.now && performance.now() * 1000) || 0;
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = Math.random() * 16;
    if (d > 0) {
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}
type KycAction = Partial<KycState>;

export const KycContext = ({ children }: Props) => {
  const [kycState, setKycState] = useReducer<
    (state: KycState, action: KycAction) => KycState
  >((state, action) => ({ ...state, ...action }), initialState);

  const { setGlobalState } = React.useContext(globalStore);
  const sessionInitRequestRef = useRef<Promise<any> | null>(null);

  useEffect(() => {
    const requestInterceptorId = axiosKycServiceClient.interceptors.request.use(
      (request: any) => {
        setGlobalState({ loading: true });
        return request;
      }
    );

    const responseInterceptorId =
      axiosKycServiceClient.interceptors.response.use(
        (response: any) => {
          setGlobalState({ loading: false });
          return response;
        },
        (error: any) => {
          setGlobalState({ loading: false });
          return Promise.reject(error);
        }
      );

    return () => {
      axiosKycServiceClient.interceptors.request.eject(requestInterceptorId);
      axiosKycServiceClient.interceptors.response.eject(responseInterceptorId);
    };
  }, [setGlobalState]);

  const advanceKyc = useCallback(() => {
    appInsightsTrackEvent({
      name: "advanceKyc",
    });

    const moveToNextKycStep = () => {
      if (
        kycState.currentStepIndex ===
        kycState.stepsConfiguration.length - 1
      ) {
        //do nothing, we have finalized the process and are showing the thank you page (if all is configured correctly)
      } else {
        setKycState({
          currentStepIndex: kycState.currentStepIndex + 1,
          currentInternalStepIndex: 0,
          currentAbsoluteStep: kycState.currentAbsoluteStep + 1,
        });
      }
    };

    const currentStep = kycAvailableSteps.find(
      (s) =>
        s.kycStep === kycState.stepsConfiguration[kycState.currentStepIndex]
    );

    if (!currentStep) return;

    if (
      kycState.currentInternalStepIndex ===
      currentStep.formsConfiguration.length - 1
    ) {
      moveToNextKycStep();
    } else {
      setKycState({
        currentInternalStepIndex: kycState.currentInternalStepIndex + 1,
        currentAbsoluteStep: kycState.currentAbsoluteStep + 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    kycState.currentInternalStepIndex,
    kycState.currentStepIndex,
    kycState.stepsConfiguration,
    kycState.currentAbsoluteStep,
  ]);

  const setKycFieldState = useCallback(
    <K extends keyof KycState>(field: K, value?: KycState[K]) => {
      const currentValue = kycState[field];
      if (typeof currentValue === "boolean") {
        setKycState({
          [field]: !currentValue,
        } as KycAction);
      } else {
        let finalValue = value;
        if (typeof finalValue === "string") {
          finalValue = finalValue.trim() as KycState[K];
        }
        setKycState({
          [field]: finalValue,
        } as KycAction);
      }
    },
    [kycState]
  );

  const startNewKycClientSession = useCallback((steps?: []) => {
    if (sessionInitRequestRef.current) {
      return sessionInitRequestRef.current;
    }

    const newClientSessionId = generateUUID();

    appInsightsTrackEvent({
      name: "startNewKycClientSession get sessionId",
      properties: {
        clientSessionId: newClientSessionId,
      },
    });

    setKycState({
      clientSessionId: newClientSessionId,
    });

    const initPromise = getSessionId(newClientSessionId)
      .then((response) => {
        appInsightsTrackEvent({
          name: "startNewKycClientSession get sessionId - success",
          properties: {
            sessionId: response.data.sessionId,
          },
        });
        if (steps) {
          setKycState({
            stepsConfiguration: steps,
          });
        } else {
          setKycState({
            stepsConfiguration: defaultStepsConfiguration,
          });
        }
        setKycState({
          contactInfoSessionId: response.data.sessionId,
          currentStepIndex: 0,
          currentInternalStepIndex: 0,
          currentAbsoluteStep: 0,
        });
        return response;
      })
      .catch((error) => {
        appInsightsTrackEvent({
          name: "startNewKycClientSession get sessionId - error",
          properties: {
            error: error,
          },
        });
        catchBody(error);
      })
      .finally(() => {
        sessionInitRequestRef.current = null;
      });

    sessionInitRequestRef.current = initPromise;
    return initPromise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const continueKycClientSession = useCallback(
    (
      clientSessionId: string,
      contactInfoSessionId: string,
      steps?: KycStep[]
    ) => {
      appInsightsTrackEvent({
        name: "continueKycClientSession Starting",
        properties: {
          clientSessionId: clientSessionId,
          contactInfoSessionId: contactInfoSessionId,
        },
      });

      setKycState({
        clientSessionId: clientSessionId,
        contactInfoSessionId: contactInfoSessionId,
      });

      if (steps && steps.length > 0) {
        appInsightsTrackEvent({
          name: "continueKycClientSession requested predefined steps",
          properties: {
            steps: steps,
          },
        });

        setKycState({
          stepsConfiguration: steps,
        });
      } else {
        steps = defaultStepsConfiguration;
        appInsightsTrackEvent({
          name: "continueKycClientSession no predefined steps",
          properties: {
            steps: steps,
          },
        });

        setKycState({
          stepsConfiguration: steps,
        });
      }
      setKycState({
        currentStepIndex: 0,
        currentInternalStepIndex: 0,
        currentAbsoluteStep: 0,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setKycState]
  );

  useEffect(() => {
    if (kycState.currentInternalStepIndex >= 0) {
      const currentStep = kycAvailableSteps.find(
        (s) =>
          s.kycStep === kycState.stepsConfiguration[kycState.currentStepIndex]
      );

      if (!currentStep) return;

      let newForm =
        currentStep.formsConfiguration[kycState.currentInternalStepIndex];
      let newCurrentInternalStepIndex = kycState.currentInternalStepIndex;
      let shouldUpdateCurrentInternalStepIndex = false;

      if (
        currentStep.kycStep === KycStep.ExternalKycProvider &&
        kycState.currentInternalStepIndex > 0 &&
        kycState.kycProviderUrls
      ) {
        let tempForm =
          currentStep.formsConfiguration[kycState.currentInternalStepIndex];
        let tempCurrentInternalStepIndex = kycState.currentInternalStepIndex;
        while (kycState.kycProviderUrls[tempForm] === undefined) {
          tempForm =
            currentStep.formsConfiguration[++tempCurrentInternalStepIndex];
        }
        newForm = tempForm;
        newCurrentInternalStepIndex = tempCurrentInternalStepIndex;
        shouldUpdateCurrentInternalStepIndex =
          kycState.currentInternalStepIndex !== tempCurrentInternalStepIndex
            ? true
            : false;
      }

      const totalSteps = kycState.stepsConfiguration.reduce(
        (acc: number, element: KycStep) => {
          const elem = kycAvailableSteps.find((s) => s.kycStep === element);

          if (!elem) return acc;

          if (
            elem.kycStep === KycStep.ExternalKycProvider &&
            kycState.kycProviderUrls
          ) {
            let externalKycProviderStepLength = 1;
            elem.formsConfiguration.forEach((kycForm) => {
              if (
                kycState.kycProviderUrls &&
                kycState.kycProviderUrls[kycForm] !== undefined
              ) {
                externalKycProviderStepLength++;
              }
            });
            return acc + externalKycProviderStepLength;
          }

          return acc + elem.formsConfiguration.length;
        },
        0
      );
      const newPercent = Math.round(
        (kycState.currentAbsoluteStep / (totalSteps - 1)) * 100
      );

      if (shouldUpdateCurrentInternalStepIndex) {
        setKycState({
          currentForm: newForm,
          percent: newPercent,
          currentInternalStepIndex: newCurrentInternalStepIndex,
        });
      } else {
        setKycState({
          currentForm: newForm,
          percent: newPercent,
        });
      }
    }
  }, [
    kycState.currentInternalStepIndex,
    kycState.currentStepIndex,
    kycState.stepsConfiguration,
    kycState.currentAbsoluteStep,
    kycState.kycProviderUrls,
  ]);

  ///Initialize app insights
  useEffect(() => {
    if (import.meta.env.VITE_APP_INSIGHTS_INSTRUMENTATION_KEY) {
      appInsightsClient = new ApplicationInsights({
        config: {
          instrumentationKey: import.meta.env
            .VITE_APP_INSIGHTS_INSTRUMENTATION_KEY,
        },
      });
      appInsightsClient.loadAppInsights();
    }
  }, []);

  const appInsightsTrackEvent = useCallback(
    (eventData: any) => {
      if (appInsightsClient) {
        eventData.properties = {
          ...eventData.properties,
          ...{
            email: kycState.email.toLowerCase(),
            phoneNumber: kycState.phoneNumber.toLowerCase(),
            contactInfoSessionId: kycState.contactInfoSessionId,
            clientSessionId: kycState.clientSessionId,
            currentStepIndex: kycState.currentStepIndex,
            currentInternalStepIndex: kycState.currentInternalStepIndex,
            phoneRegistrationId: kycState.phoneRegistrationId,
            emailRegistrationId: kycState.emailRegistrationId,
          },
        };
        appInsightsClient.trackEvent(eventData);
      }
    },
    [kycState]
  );

  return (
    <Provider
      value={{
        kycState,
        setKycState,
        advanceKyc,
        setKycFieldState,
        startNewKycClientSession,
        continueKycClientSession,
        appInsightsTrackEvent,
      }}
    >
      {children}
    </Provider>
  );
};
